// src/pages/DeadlinePage.jsx
// Pagina per registrare una nuova scadenza su un veicolo.
// Chiede: tipo → data esecuzione → ricorrenza (con default da settings)
// → calcola automaticamente la data scadenza → salva.

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import {
  addDeadline, calcolaScadenza, RECURRENCES,
  isBolloEsente, getVehicles
} from '../lib/dataService';
import { useAuth } from '../hooks/useAuth';
import { format, addDays } from 'date-fns';

const TIPO_LABELS = {
  bollo:          { label: 'Bollo',               defaultRic: '1y', desc: 'Tassa di proprietà annuale' },
  assicurazione:  { label: 'Assicurazione RC',    defaultRic: '1y', desc: 'Polizza RC e garanzie aggiuntive' },
  collaudo:       { label: 'Collaudo / Revisione',defaultRic: '2y', desc: 'Revisione biennale obbligatoria' },
  messa_in_moto:  { label: 'Messa in moto',       defaultRic: 'free',desc: 'Promemoria avviamento stagionale' },
  certificazione: { label: 'Certificazione',      defaultRic: '2y', desc: 'Rinnovo ASI / FMI / CRS' },
};

export default function DeadlinePage() {
  const { vehicleId } = useParams();
  const { profile }   = useAuth();
  const { settings }  = useSettings();
  const navigate      = useNavigate();

  const [vehicle,        setVehicle]        = useState(null);
  const [step,           setStep]           = useState(1); // 1=tipo, 2=esecuzione, 3=scadenza, 4=conferma
  const [tipo,           setTipo]           = useState('');
  const [dataEsecuzione, setDataEsecuzione] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [ricorrenza,     setRicorrenza]     = useState('');
  const [dataScadenza,   setDataScadenza]   = useState('');
  const [note,           setNote]           = useState('');
  const [saving,         setSaving]         = useState(false);

  useEffect(() => {
    if (!profile) return;
    getVehicles(profile).then(vv => setVehicle(vv.find(v => v.id === vehicleId) || null));
  }, [vehicleId, profile]);

  // Aggiorna ricorrenza default quando cambia tipo
  useEffect(() => {
    if (!tipo) return;
    const fromSettings = settings?.tipi?.[tipo]?.ricorrenzaDefault;
    const fromDefault  = TIPO_LABELS[tipo]?.defaultRic || '1y';
    const ric = fromSettings || fromDefault;
    setRicorrenza(ric);
    if (ric !== 'free' && dataEsecuzione) {
      const calcolata = calcolaScadenza(dataEsecuzione, ric);
      if (calcolata) setDataScadenza(format(calcolata, 'yyyy-MM-dd'));
    }
  }, [tipo]);

  // Ricalcola scadenza quando cambia data esecuzione o ricorrenza
  useEffect(() => {
    if (!dataEsecuzione || !ricorrenza || ricorrenza === 'free') return;
    const calcolata = calcolaScadenza(dataEsecuzione, ricorrenza);
    if (calcolata) setDataScadenza(format(calcolata, 'yyyy-MM-dd'));
  }, [dataEsecuzione, ricorrenza]);

  const tipiAbilitati = Object.entries(TIPO_LABELS).filter(([k]) => {
    if (settings?.tipi?.[k]?.attivo === false) return false;
    if (k === 'bollo' && vehicle && isBolloEsente(vehicle.annoImmatricolazione)) return false;
    return true;
  });

  const handleSave = async () => {
    if (!dataScadenza) return;
    setSaving(true);
    try {
      await addDeadline(vehicleId, {
        tipo,
        dataEsecuzione: new Date(dataEsecuzione),
        dataScadenza:   new Date(dataScadenza),
        ricorrenza,
        note,
        attiva: true,
      });
      navigate(`/garage/${vehicleId}`);
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  };

  const ricLabel = RECURRENCES.find(r => r.value === ricorrenza)?.label || ricorrenza;
  const tipoInfo = TIPO_LABELS[tipo];

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <span style={S.logo} onClick={() => navigate(`/garage/${vehicleId}`)}>← Torna al veicolo</span>
        {vehicle && <span style={S.vehicleName}>{vehicle.marca} {vehicle.modello}</span>}
      </div>

      <div style={S.content}>
        <h2 style={S.title}>Registra scadenza</h2>

        {/* STEP INDICATOR */}
        <div style={S.stepBar}>
          {['Tipo','Data','Scadenza','Conferma'].map((l, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap: 6 }}>
              <div style={{
                ...S.stepDot,
                background: step > i+1 ? '#2e7d32' : step === i+1 ? '#1a1a1a' : '#e0e0e0',
                color: step >= i+1 ? '#fff' : '#bbb',
              }}>
                {step > i+1 ? '✓' : i+1}
              </div>
              <span style={{ fontSize: 13, color: step === i+1 ? '#1a1a1a' : '#bbb', fontWeight: step===i+1?600:400 }}>{l}</span>
              {i < 3 && <div style={S.stepLine} />}
            </div>
          ))}
        </div>

        {/* STEP 1 — TIPO */}
        {step === 1 && (
          <div style={S.card}>
            <div style={S.cardTitle}>Che tipo di scadenza vuoi registrare?</div>
            <div style={S.tipoGrid}>
              {tipiAbilitati.map(([k, cfg]) => (
                <button
                  key={k}
                  style={{ ...S.tipoBtn, ...(tipo===k ? S.tipoBtnActive : {}) }}
                  onClick={() => setTipo(k)}
                >
                  <div style={S.tipoBtnLabel}>{cfg.label}</div>
                  <div style={S.tipoBtnDesc}>{cfg.desc}</div>
                </button>
              ))}
            </div>
            <button
              style={{ ...S.btn, opacity: tipo ? 1 : .4 }}
              disabled={!tipo}
              onClick={() => setStep(2)}
            >
              Avanti →
            </button>
          </div>
        )}

        {/* STEP 2 — DATA ESECUZIONE */}
        {step === 2 && (
          <div style={S.card}>
            <div style={S.cardTitle}>Quando è stato fatto / pagato?</div>
            <div style={S.cardSub}>{tipoInfo?.label}</div>
            <label style={S.label}>Data esecuzione</label>
            <input
              type="date"
              style={S.input}
              value={dataEsecuzione}
              onChange={e => setDataEsecuzione(e.target.value)}
            />
            <div style={S.hint}>Lascia la data di oggi se lo stai registrando ora.</div>
            <div style={S.btnRow}>
              <button style={S.btnSec} onClick={() => setStep(1)}>← Indietro</button>
              <button style={S.btn} onClick={() => setStep(3)}>Avanti →</button>
            </div>
          </div>
        )}

        {/* STEP 3 — SCADENZA */}
        {step === 3 && (
          <div style={S.card}>
            <div style={S.cardTitle}>Quando scade?</div>
            <div style={S.cardSub}>Il sistema ha calcolato automaticamente la scadenza. Puoi modificarla.</div>

            <label style={S.label}>Ricorrenza</label>
            <select
              style={S.select}
              value={ricorrenza}
              onChange={e => setRicorrenza(e.target.value)}
            >
              {RECURRENCES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>

            <label style={{ ...S.label, marginTop: 16 }}>Data scadenza</label>
            <input
              type="date"
              style={S.input}
              value={dataScadenza}
              onChange={e => setDataScadenza(e.target.value)}
            />

            {ricorrenza !== 'free' && dataScadenza && (
              <div style={S.infoBox}>
                Calcolata automaticamente: {dataEsecuzione} + {ricLabel} = <strong>{dataScadenza}</strong>
              </div>
            )}

            <label style={{ ...S.label, marginTop: 16 }}>Note (opzionale)</label>
            <input
              type="text"
              style={S.input}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="es. numero polizza, officina, ecc."
            />

            <div style={S.btnRow}>
              <button style={S.btnSec} onClick={() => setStep(2)}>← Indietro</button>
              <button
                style={{ ...S.btn, opacity: dataScadenza ? 1 : .4 }}
                disabled={!dataScadenza}
                onClick={() => setStep(4)}
              >
                Avanti →
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — CONFERMA */}
        {step === 4 && (
          <div style={S.card}>
            <div style={S.cardTitle}>Conferma e salva</div>
            <div style={S.recap}>
              <RecapRow label="Tipo"           value={tipoInfo?.label} />
              <RecapRow label="Eseguito il"    value={dataEsecuzione} />
              <RecapRow label="Scade il"       value={dataScadenza} />
              <RecapRow label="Ricorrenza"     value={ricLabel} />
              {note && <RecapRow label="Note" value={note} />}
            </div>
            <div style={S.btnRow}>
              <button style={S.btnSec} onClick={() => setStep(3)}>← Modifica</button>
              <button
                style={{ ...S.btn, opacity: saving ? .6 : 1 }}
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? 'Salvo…' : '✓ Salva scadenza'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RecapRow({ label, value }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #f0f0f0' }}>
      <span style={{ fontSize:15, color:'#888' }}>{label}</span>
      <span style={{ fontSize:15, fontWeight:600, color:'#1a1a1a' }}>{value || '—'}</span>
    </div>
  );
}

const S = {
  page:    { minHeight:'100vh', background:'#f5f4f0', fontFamily:"'Georgia', serif" },
  topbar:  { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', background:'#fff', borderBottom:'1px solid #e8e8e8' },
  logo:    { fontSize:15, cursor:'pointer', color:'#185FA5' },
  vehicleName: { fontSize:15, fontWeight:600, color:'#1a1a1a' },
  content: { padding:20, maxWidth:560, margin:'0 auto' },
  title:   { fontSize:24, fontWeight:600, margin:'20px 0 20px', color:'#1a1a1a' },
  stepBar: { display:'flex', alignItems:'center', gap:4, marginBottom:24, flexWrap:'wrap' },
  stepDot: { width:26, height:26, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600, flexShrink:0 },
  stepLine:{ flex:1, height:1, background:'#e0e0e0', minWidth:12 },
  card:    { background:'#fff', borderRadius:16, padding:'24px', border:'1px solid #e8e8e8' },
  cardTitle:{ fontSize:20, fontWeight:600, marginBottom:6, color:'#1a1a1a' },
  cardSub: { fontSize:15, color:'#888', marginBottom:20 },
  tipoGrid:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:24 },
  tipoBtn: { padding:'14px 16px', borderRadius:12, border:'1.5px solid #e8e8e8', background:'#fafafa', cursor:'pointer', textAlign:'left', fontFamily:'inherit', transition:'all .15s' },
  tipoBtnActive: { borderColor:'#1a1a1a', background:'#f5f5f5' },
  tipoBtnLabel: { fontSize:15, fontWeight:600, color:'#1a1a1a', marginBottom:4 },
  tipoBtnDesc:  { fontSize:13, color:'#888' },
  label:   { display:'block', fontSize:13, fontWeight:500, color:'#555', marginBottom:6, marginTop:16 },
  input:   { width:'100%', padding:'12px 14px', fontSize:16, border:'1.5px solid #e0e0e0', borderRadius:10, outline:'none', fontFamily:'inherit', boxSizing:'border-box', background:'#fafafa', color:'#1a1a1a' },
  select:  { width:'100%', padding:'12px 14px', fontSize:16, border:'1.5px solid #e0e0e0', borderRadius:10, outline:'none', fontFamily:'inherit', boxSizing:'border-box', background:'#fafafa', color:'#1a1a1a', cursor:'pointer' },
  hint:    { fontSize:13, color:'#aaa', marginTop:6 },
  infoBox: { background:'#e6f1fb', borderRadius:8, padding:'10px 14px', fontSize:14, color:'#185FA5', marginTop:12 },
  btn:     { flex:1, padding:'14px', background:'#1a1a1a', color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  btnSec:  { flex:1, padding:'14px', background:'none', color:'#1a1a1a', border:'1.5px solid #e0e0e0', borderRadius:10, fontSize:16, cursor:'pointer', fontFamily:'inherit' },
  btnRow:  { display:'flex', gap:10, marginTop:24 },
  recap:   { background:'#fafafa', borderRadius:12, padding:'4px 16px', marginBottom:24 },
};
